/* eslint-disable camelcase */
import React from 'react'
import { Button, Collapse, Input, Select, Typography } from 'antd'
import PropTypes from 'prop-types'

import { ImageUploader } from '~components'
import {
  CONCESSION_GALLERY_VARIANTS,
  getConcessionGalleryItemLimits,
  getDefaultConcessionGalleryItem
} from '../../utils/concessionGalleryItemDefaults'
import styles from '../FAQPageSections/FAQAdminFields.module.css'
import DownArrow from '../PositionArrows/DownArrow'
import UpArrow from '../PositionArrows/UpArrow'
import ConcessionBadgeFields from './ConcessionBadgeFields'

const { Panel } = Collapse
const { Text } = Typography
const { TextArea } = Input

const MEDIA_TYPE_OPTIONS = [
  { label: 'Image', value: 'image' },
  { label: 'Video', value: 'video' }
]

const CTA_TARGET_OPTIONS = [
  { label: 'Same tab', value: '_self' },
  { label: 'New tab', value: '_blank' }
]

const getCardLabel = (item, index, variantType) => {
  if (variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT) {
    return item?.title?.trim() || 'Spotlight Item'
  }

  return item?.title?.trim() || `Card ${index + 1}`
}

const ConcessionGalleryItemsEditor = ({
  galleryItems = [],
  itemErrors = [],
  onChange,
  variantType
}) => {
  const { min, max } = getConcessionGalleryItemLimits(variantType)
  const showCardTextFields = variantType !== CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL
  const showCtaFields = variantType !== CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL
  const showBadgeFields = variantType !== CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL
  const cardHeaderLabel = variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT
    ? 'Spotlight Header'
    : 'Carousel Card Header'
  const cardDescriptionLabel = variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT
    ? 'Spotlight Description'
    : 'Carousel Card Description'

  const updateItems = nextItems => onChange(nextItems)

  const updateItem = (index, partial) => {
    const nextItems = galleryItems.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...partial } : item
    ))
    updateItems(nextItems)
  }

  const handleAddItem = () => {
    if (galleryItems.length >= max) {
      return
    }
    updateItems([...galleryItems, getDefaultConcessionGalleryItem()])
  }

  const handleDeleteItem = index => {
    if (galleryItems.length <= min) {
      return
    }
    updateItems(galleryItems.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleMoveUp = (event, index) => {
    event.stopPropagation()
    if (index <= 0) {
      return
    }
    const nextItems = [...galleryItems]
    ;[nextItems[index - 1], nextItems[index]] = [nextItems[index], nextItems[index - 1]]
    updateItems(nextItems)
  }

  const handleMoveDown = (event, index) => {
    event.stopPropagation()
    if (index >= galleryItems.length - 1) {
      return
    }
    const nextItems = [...galleryItems]
    ;[nextItems[index + 1], nextItems[index]] = [nextItems[index], nextItems[index + 1]]
    updateItems(nextItems)
  }

  const handleBadgeChange = (index, badgeValue) => {
    updateItem(index, { badge: badgeValue })
  }

  return (
    <div className={styles.fieldGroup}>
      <div className={styles.toolbar}>
        <Text className={styles.toolbarMeta}>
          {variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT
            ? 'Configure 1 spotlight item.'
            : `Configure ${min}-${max} carousel cards.`}
        </Text>
        {variantType !== CONCESSION_GALLERY_VARIANTS.SPOTLIGHT && (
          <Button disabled={galleryItems.length >= max} onClick={handleAddItem} type="primary">
            Add Card
          </Button>
        )}
      </div>

      <Collapse accordion expandIconPosition="right">
        {galleryItems.map((item, index) => {
          const errors = itemErrors[index] || {}
          const mediaError = errors.src || errors.videoUrl

          return (
            <Panel
              key={`gallery-item-${index}`}
              extra={(
                <div className={styles.panelActions} onClick={event => event.stopPropagation()}>
                  {index > 0 && (
                    <span data-testid="up-arrow" onClick={event => handleMoveUp(event, index)}>
                      <UpArrow />
                    </span>
                  )}
                  {index < galleryItems.length - 1 && (
                    <span data-testid="down-arrow" onClick={event => handleMoveDown(event, index)}>
                      <DownArrow />
                    </span>
                  )}
                  {galleryItems.length > min && (
                    <Button danger onClick={() => handleDeleteItem(index)} size="small">
                      Delete
                    </Button>
                  )}
                </div>
              )}
              header={getCardLabel(item, index, variantType)}
            >
              <div className={styles.fieldRow}>
                <Text className={styles.fieldLabel}>Media Type</Text>
                <Select
                  onChange={mediaType => updateItem(index, {
                    mediaType,
                    src: mediaType === item.mediaType ? item.src : ''
                  })}
                  options={MEDIA_TYPE_OPTIONS}
                  style={{ width: '100%' }}
                  value={item.mediaType || 'image'}
                />
              </div>

              {item.mediaType === 'video' ? (
                <div className={styles.fieldRow}>
                  <Text className={`${styles.fieldLabel}${mediaError ? ` ${styles.fieldLabelError}` : ''}`}>YouTube Video URL</Text>
                  <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }} type="secondary">
                    Use the embed URL format: https://www.youtube.com/embed/VIDEO_ID
                  </Text>
                  <div className={mediaError ? styles.fieldControlError : undefined}>
                    <Input
                      onChange={event => updateItem(index, { src: event.target.value })}
                      placeholder="https://www.youtube.com/embed/dQw4w9WgXcQ"
                      status={mediaError ? 'error' : undefined}
                      value={item.src || ''}
                    />
                  </div>
                  {mediaError && <Text className={styles.fieldError}>{mediaError}</Text>}
                </div>
              ) : (
                <div className={styles.fieldRow}>
                  <Text className={`${styles.fieldLabel}${mediaError ? ` ${styles.fieldLabelError}` : ''}`}>Image</Text>
                  <div className={mediaError ? styles.fieldControlError : undefined}>
                    <ImageUploader
                      alt={item.alt || ''}
                      handleIconChange={({ alt, url }) => updateItem(index, {
                        src: url || item.src || '',
                        alt: alt ?? item.alt ?? ''
                      })}
                      handleUpload={url => updateItem(index, { src: url })}
                      logoUrl={item.src}
                      pageType="concession"
                    />
                  </div>
                  {mediaError && <Text className={styles.fieldError}>{mediaError}</Text>}
                </div>
              )}

              {item.mediaType !== 'video' && (
                <div className={styles.fieldRow}>
                  <Text className={styles.fieldLabel}>Image Alt Text</Text>
                  <Input
                    onChange={event => updateItem(index, { alt: event.target.value })}
                    placeholder="Describe the image for accessibility"
                    value={item.alt || ''}
                  />
                </div>
              )}

              {showBadgeFields && (
                <ConcessionBadgeFields
                  badge={item.badge}
                  itemErrors={itemErrors}
                  onChange={badgeValue => handleBadgeChange(index, badgeValue)}
                />
              )}

              {showCardTextFields && (
                <>
                  <div className={styles.fieldRow}>
                    <Text className={`${styles.fieldLabel}${errors.title ? ` ${styles.fieldLabelError}` : ''}`}>{cardHeaderLabel}</Text>
                    <div className={errors.title ? styles.fieldControlError : undefined}>
                      <Input
                        onChange={event => updateItem(index, { title: event.target.value })}
                        placeholder={variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT ? 'Fuku' : 'Vendor name'}
                        status={errors.title ? 'error' : undefined}
                        value={item.title || ''}
                      />
                    </div>
                    {errors.title && <Text className={styles.fieldError}>{errors.title}</Text>}
                  </div>

                  <div className={styles.fieldRow}>
                    <Text className={`${styles.fieldLabel}${errors.subtitle ? ` ${styles.fieldLabelError}` : ''}`}>{cardDescriptionLabel}</Text>
                    <div className={errors.subtitle ? styles.fieldControlError : undefined}>
                      <TextArea
                        onChange={event => updateItem(index, { subtitle: event.target.value })}
                        placeholder={variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT
                          ? 'Short spotlight description'
                          : 'Short card description'}
                        rows={3}
                        status={errors.subtitle ? 'error' : undefined}
                        value={item.subtitle || ''}
                      />
                    </div>
                    {errors.subtitle && <Text className={styles.fieldError}>{errors.subtitle}</Text>}
                  </div>
                </>
              )}

              {showCtaFields && (
                <>
                  <div className={styles.fieldRow}>
                    <Text className={styles.fieldLabel}>CTA Label</Text>
                    <Input
                      onChange={event => updateItem(index, {
                        cta: {
                          ...(item.cta || {}),
                          label: event.target.value
                        }
                      })}
                      placeholder="View Menu"
                      value={item.cta?.label || ''}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <Text className={styles.fieldLabel}>CTA URL</Text>
                    <Input
                      onChange={event => updateItem(index, {
                        cta: {
                          ...(item.cta || {}),
                          url: event.target.value
                        }
                      })}
                      placeholder="https://..."
                      value={item.cta?.url || ''}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <Text className={styles.fieldLabel}>CTA Target</Text>
                    <Select
                      onChange={target => updateItem(index, {
                        cta: {
                          ...(item.cta || {}),
                          target
                        }
                      })}
                      options={CTA_TARGET_OPTIONS}
                      style={{ width: '100%' }}
                      value={item.cta?.target || '_self'}
                    />
                  </div>
                </>
              )}
            </Panel>
          )
        })}
      </Collapse>
    </div>
  )
}

ConcessionGalleryItemsEditor.propTypes = {
  galleryItems: PropTypes.array,
  itemErrors: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  variantType: PropTypes.string.isRequired
}

export default ConcessionGalleryItemsEditor
